import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { canEdit } from "../../lib/permissions";
import { SlidePanel } from "../SlidePanel";
import { Empty } from "../UI";
import { HandoutSources } from "./HandoutSources";
import { HandoutCard } from "./HandoutCard";
import { HandoutEditor } from "./HandoutEditor";

const CREATED = { ai: "AI draft ready for review", clinic: "Clinic handout added as a draft", public: "Link handout added as a draft" };

/** Patient education for one diagnosis: create from three sources, approve, then print, email, or share to the portal. */
export function PatientEducationPanel({ dx, client, onClose }) {
  const { user } = useAuth();
  const editable = canEdit(user, "clinical");
  const [opts, setOpts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => (dx ? api.get(`/diagnoses/${dx.id}/education/options`).then((r) => setOpts(r.data)).catch((e) => toast.error(apiError(e))) : null), [dx]);
  useEffect(() => { setOpts(null); setEditing(null); load(); }, [load]);
  const create = async (body) => {
    setBusy(true);
    try { const r = await api.post(`/diagnoses/${dx.id}/handouts`, body); toast.success(CREATED[body.source]); setEditing(r.data); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <SlidePanel open={!!dx} onClose={onClose} title="Patient Education" width="max-w-2xl" testId="education-panel"
      subtitle={dx ? `${dx.icd10_code || dx.code || ""} ${dx.description}`.trim() : ""}>
      {!opts ? <div className="h-32 animate-pulse rounded-2xl bg-[#F5F2EB]" /> : editing ? (
        <HandoutEditor h={editing} onCancel={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />
      ) : (
        <div className="space-y-5" data-testid="education-content">
          <div>
            <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-[#7B6157]">Handouts for this diagnosis ({opts.given.length})</div>
            {opts.given.length ? <div className="space-y-3">{opts.given.map((h) => (
              <HandoutCard key={h.id} h={h} client={client} editable={editable} isAdmin={user?.role === "admin"} onEdit={setEditing} onChanged={load} />
            ))}</div> : <Empty text="No handouts given for this diagnosis yet" />}
          </div>
          {editable && <HandoutSources options={opts} busy={busy} onCreate={create} />}
        </div>
      )}
    </SlidePanel>
  );
}
